import React, { useEffect, useState } from "react";
import { useRoleVisibilityStore } from "../store/role-visibility-store";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import { useToast } from "@shared/ui/use-toast";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { ChevronDown, ChevronUp, Info, InfoIcon } from "lucide-react";
import { useRolesPermission, useUpdateRoleConfiguration } from "../../../hooks"; // adjust path as needed
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";

export function EnhancedRoleVisibilityManager() {
  const { visibilitySettings, toggleDepartment, toggleRole } =
    useRoleVisibilityStore();
  const [expandedDepts, setExpandedDepts] = useState<string[]>([]);
  const [localState, setLocalState] = useState<any>({});
  const [originalState, setOriginalState] = useState<any>({}); // Track original state for change detection
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();

  const { data: departmentRoles = [] } = useRolesPermission();
  console.log("departmentRoles", departmentRoles);
  const updateRoleConfigMutation = useUpdateRoleConfiguration();

  // Initialize original state when data loads
  useEffect(() => {
    if (departmentRoles.length > 0 && Object.keys(originalState).length === 0) {
      const initialState = {};
      departmentRoles.forEach((deptData) => {
        const department = deptData.department;
        // Department is checked if any role is active
        const deptChecked = deptData.roles.some((role) => role.isActive);

        initialState[department] = {
          enabled: deptChecked,
          roles: {},
        };

        deptData.roles.forEach((role) => {
          initialState[department].roles[role.roleName] = role.isActive;
        });
      });

      setOriginalState(initialState);
      setLocalState(initialState); // Initialize local state with API data
      console.log("🔄 Initialized original state:", initialState);
    }
  }, [departmentRoles, originalState]);

  // Debug logging
  useEffect(() => {
    console.log("🔍 Current visibilitySettings:", visibilitySettings);
    console.log("🔍 Department roles data:", departmentRoles);
    console.log("🔍 Local state:", localState);
    console.log("🔍 Original state:", originalState);
  }, [visibilitySettings, departmentRoles, localState, originalState]);

  const handleToggleDepartment = (department: string, checked: boolean) => {
    console.log("🔍 Toggling department:", department, "to:", checked);

    // Update local state immediately for UI responsiveness
    setLocalState((prev) => ({
      ...prev,
      [department]: {
        ...prev[department],
        enabled: checked,
        // When toggling department, update all roles within it
        roles: Object.fromEntries(
          Object.keys(prev[department]?.roles || {}).map((role) => [
            role,
            checked,
          ])
        ),
      },
    }));

    // Update store
    toggleDepartment(department, checked);
  };

  const handleToggleRole = (
    department: string,
    role: string,
    checked: boolean
  ) => {
    console.log(
      "🔍 Toggling role:",
      role,
      "in department:",
      department,
      "to:",
      checked
    );

    // Update local state immediately for UI responsiveness
    setLocalState((prev) => {
      const newRoles = {
        ...prev[department]?.roles,
        [role]: checked,
      };

      // Check if any role in this department is active to determine department state
      const anyRoleActive = Object.values(newRoles).some(
        (roleActive) => roleActive
      );

      return {
        ...prev,
        [department]: {
          ...prev[department],
          enabled: anyRoleActive, // Department enabled if any role is active
          roles: newRoles,
        },
      };
    });

    // Update store
    const currentDeptState = getDepartmentCheckedState(department);
    const newDeptState =
      checked ||
      Object.values(localState[department]?.roles || {}).some((r) => r);

    if (newDeptState !== currentDeptState) {
      toggleDepartment(department, newDeptState);
    }
    toggleRole(department, role, checked);
  };

  // Function to handle role selection across all departments (like in Angular)
  const handleRoleSelectAcrossDepartments = (
    roleId: number,
    checked: boolean
  ) => {
    console.log("🔍 Toggling role across departments:", roleId, "to:", checked);

    setLocalState((prev) => {
      const newState = { ...prev };

      // Update all matching roles across departments
      departmentRoles.forEach((deptData) => {
        const department = deptData.department;
        const roleInDept = deptData.roles.find((r) => r.role === roleId);

        if (roleInDept) {
          if (!newState[department]) {
            newState[department] = { enabled: false, roles: {} };
          }

          newState[department].roles = {
            ...newState[department].roles,
            [roleInDept.roleName]: checked,
          };

          // Update department status based on any role being active
          const anyRoleActive = Object.values(newState[department].roles).some(
            (roleActive) => roleActive
          );
          newState[department].enabled = anyRoleActive;
        }
      });

      return newState;
    });
  };

  // Function to get departments that have changed
  const getDepartmentsToUpdate = () => {
    const updatedDepartments = [];

    departmentRoles.forEach((deptData) => {
      const department = deptData.department;
      const currentState = getCurrentState(department);
      const originalDeptState = originalState[department];

      // Check if department state has changed
      const deptStateChanged =
        currentState.enabled !== originalDeptState?.enabled;

      // Check if any permissions within the department have changed
      const permissionsChanged = deptData.roles.some((role) => {
        const currentRoleState = currentState.roles[role.roleName];
        const originalRoleState = originalDeptState?.roles[role.roleName];
        return currentRoleState !== originalRoleState;
      });

      if (deptStateChanged || permissionsChanged) {
        updatedDepartments.push({
          department: department,
          is_active: currentState.enabled,
          roles: deptData.roles.map((role) => ({
            role_id: role.role,
            is_active: currentState.roles[role.roleName] || false,
          })),
        });
      }
    });

    return updatedDepartments;
  };

  // Function to get roles that have changed
  const getRolesToUpdate = () => {
    const updatedRoles = [];

    departmentRoles.forEach((deptData) => {
      const department = deptData.department;
      const currentState = getCurrentState(department);
      const originalDeptState = originalState[department];

      deptData.roles.forEach((role) => {
        const currentRoleState = currentState.roles[role.roleName];
        const originalRoleState = originalDeptState?.roles[role.roleName];

        if (currentRoleState !== originalRoleState) {
          updatedRoles.push({
            role_id: role.role,
            is_active: currentRoleState || false,
          });
        }
      });
    });

    return updatedRoles;
  };

  // Helper function to get current state (local state takes precedence)
  const getCurrentState = (department: string) => {
    return {
      enabled:
        localState[department]?.enabled ??
        originalState[department]?.enabled ??
        false,
      roles: {
        ...originalState[department]?.roles,
        ...localState[department]?.roles,
      },
    };
  };

  // Function to update original state after successful save
  const updateOriginalState = () => {
    const newOriginalState = {};
    departmentRoles.forEach((deptData) => {
      const department = deptData.department;
      const currentState = getCurrentState(department);
      newOriginalState[department] = currentState;
    });
    setOriginalState(newOriginalState);
    setLocalState({}); // Clear local state after save
    console.log("🔄 Updated original state after save:", newOriginalState);
  };

  const handleToggleExpand = (department: string) => {
    setExpandedDepts((prev) =>
      prev.includes(department)
        ? prev.filter((item) => item !== department)
        : [...prev, department]
    );
  };

  const handleSave = async () => {
    try {
      console.log("💾 Saving settings...");

      if (!remoteUtilityId) {
        toast({
          title: "Error",
          description: "Remote utility ID not found",
          variant: "destructive",
        });
        return;
      }

      // Get only the changes
      const departmentsToUpdate = getDepartmentsToUpdate();
      const rolesToUpdate = getRolesToUpdate();

      // Build payload with only changed data
      const payload: any = {
        remote_utility_id: Number(remoteUtilityId),
      };

      // Only include if there are changes
      if (departmentsToUpdate.length > 0) {
        payload.departments_to_update = departmentsToUpdate;
      }

      if (rolesToUpdate.length > 0) {
        payload.roles_to_update = rolesToUpdate;
      }

      // Check if there are any changes to save
      if (departmentsToUpdate.length === 0 && rolesToUpdate.length === 0) {
        toast({
          title: "No changes",
          description: "No changes detected to save",
        });
        return;
      }

      console.log("🚀 Sending payload:", JSON.stringify(payload, null, 2));
      console.log("📊 Departments to update:", departmentsToUpdate.length);
      console.log("📊 Roles to update:", rolesToUpdate.length);

      await updateRoleConfigMutation.mutateAsync(payload, {
        onSuccess: () => {
          logEvent("System Roles Update", {
            ...payload,
          });
        },
      });

      // Update original state after successful save
      updateOriginalState();

      toast({
        title: "Settings saved",
        description: `Successfully updated ${departmentsToUpdate.length} departments and ${rolesToUpdate.length} roles`,
      });
    } catch (error) {
      console.error("❌ Error saving settings:", error);
      toast({
        title: "Error",
        description: "Failed to save role configuration. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Helper function to get checked state with fallbacks
  const getDepartmentCheckedState = (department: string) => {
    return getCurrentState(department).enabled;
  };

  const getRoleCheckedState = (department: string, roleName: string) => {
    return getCurrentState(department).roles[roleName] || false;
  };

  return (
    <div className="space-y-4  border rounded-md p-4">
      {/* Debug Panel - Remove in production */}
      {/* <Card className="bg-gray-50 border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-gray-600">Debug Info (Remove in production)</CardTitle>
        </CardHeader>
        <CardContent className="text-xs">
          <div className="space-y-2">
            <div>
              <strong>Visibility Settings:</strong>
              <pre className="mt-1 text-xs bg-white p-2 rounded overflow-auto max-h-32">
                {JSON.stringify(visibilitySettings, null, 2)}
              </pre>
            </div>
            <div>
              <strong>Local State:</strong>
              <pre className="mt-1 text-xs bg-white p-2 rounded overflow-auto max-h-32">
                {JSON.stringify(localState, null, 2)}
              </pre>
            </div>
            <div>
              <strong>Original State:</strong>
              <pre className="mt-1 text-xs bg-white p-2 rounded overflow-auto max-h-32">
                {JSON.stringify(originalState, null, 2)}
              </pre>
            </div>
            <div>
              <strong>Department Roles Count:</strong> {departmentRoles.length}
            </div>
            <div>
              <strong>Has Changes:</strong> {(getDepartmentsToUpdate().length > 0 || getRolesToUpdate().length > 0).toString()}
            </div>
          </div>
        </CardContent>
      </Card> */}
       <div className="flex flex-col">
         <h3 className="card-heading-text">System Roles</h3>
          <p className="card-subheading-text">
           Select departments and select roles for user access.
</p>
        </div>


      <Accordion type="single" collapsible className="mb-4">
        <AccordionItem value="help">
          <AccordionTrigger className="text-sm font-medium">
            <div className="flex items-center">
              <InfoIcon className="h-4 w-4 mr-2" />
              Need help with system roles?
            </div>
          </AccordionTrigger>
          <AccordionContent className="text-sm text-muted-foreground">
            <p className="mb-2">
              Configure departments and roles for your utility in SMART360:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Assign departments to match your utility’s structure.</li>
              <li>
                Configure roles with access to specific modules and menus.
              </li>
              <li>Control user access based on department and role.</li>
              <li>
                Ensure permissions align with operational needs and security
                policies.
              </li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      <div className="grid grid-cols-1 gap-4">
        {departmentRoles.map((deptData) => {
          const isDeptExpanded = expandedDepts.includes(deptData.department);
          const isDeptEnabled = getDepartmentCheckedState(deptData.department);

          console.log(
            `🔍 Rendering department: ${deptData.department}, enabled: ${isDeptEnabled}`
          );

          return (
            <div>
              <Card
                key={deptData.department}
                className={!isDeptEnabled ? "opacity-80" : ""}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`dept-${deptData.department}`}
                        checked={isDeptEnabled}
                        onCheckedChange={(checked) => {
                          console.log("🎯 Checkbox change event:", checked);
                          handleToggleDepartment(
                            deptData.department,
                            !!checked
                          );
                        }}
                      />
                      <label
                        htmlFor={`dept-${deptData.departmentName}`}
                        className="text-base"
                      >
                        {deptData.departmentName}
                      </label>

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-poppins">
                              {deptData.roles.length} roles in this department
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleExpand(deptData.department)}
                      className="h-8 w-8 p-0"
                    >
                      {isDeptExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </CardHeader>

                {isDeptExpanded && (
                  <CardContent>
                    <div className="grid gap-2 pl-6">
                      {deptData.roles.map((role) => {
                        const isRoleEnabled = getRoleCheckedState(
                          deptData.department,
                          role.roleName
                        );

                        console.log(
                          `🔍 Rendering role: ${role.roleName}, enabled: ${isRoleEnabled}, dept enabled: ${isDeptEnabled}`
                        );

                        return (
                          <div
                            key={role.roleName}
                            className="flex items-start space-x-2 group p-2 hover:bg-muted/30 rounded-md"
                          >
                            <Checkbox
                              id={`role-${deptData.department}-${role.roleName}`}
                              checked={isRoleEnabled}
                              // Remove disabled state to allow role checking without department
                              onCheckedChange={(checked) => {
                                console.log(
                                  "🎯 Role checkbox change event:",
                                  checked
                                );
                                handleToggleRole(
                                  deptData.department,
                                  role.roleName,
                                  !!checked
                                );
                              }}
                            />
                            <div className="grid gap-1">
                              <label
                                htmlFor={`role-${deptData.department}-${role.roleName}`}
                                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-poppins cursor-pointer"
                              >
                                {role.roleName}
                              </label>
                              {role.description && (
                                <p className="text-xs text-muted-foreground font-poppins">
                                  {role.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          );
        })}
      </div>

      {departmentRoles.length === 0 && (
        <div className="flex items-center justify-center h-32 border rounded-md bg-muted/10">
          <p className="text-muted-foreground font-poppins">
            No departments or roles found
          </p>
        </div>
      )}

      <div className="flex justify-end space-x-2">
        <Button
          onClick={handleSave}
          className="font-poppins"
          disabled={updateRoleConfigMutation.isPending}
        >
          {updateRoleConfigMutation.isPending ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
